import type { InjectionKey, MaybeRefOrGetter, Ref } from 'vue';
import type { AnyItem, CompositePrimaryKey, GlobalSingleRelationFieldId, RelationOperationToMany, RelationOperationToOne, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { CreateItem } from './relation-field-state-primitives';
import { computed, inject, provide, toValue } from 'vue';
import { noop } from '#layers/base/public';
import { useWorkspaceSchemaStore } from '#layers/data-model/public';
import { useBaseFormField } from './use-base-form-field-state';

export interface RelationFormFieldContext<Kind extends 'to-many' | 'to-one' | never = never, RelationFieldValue extends RelationOperationToMany[] | RelationOperationToOne = Kind extends 'to-many' ? RelationOperationToMany[] : Kind extends 'to-one' ? RelationOperationToOne : RelationOperationToMany[] | RelationOperationToOne> {
	field: Ref<ResolvedMonospaceSingleRelationField | null>;
	fieldValue: Ref<RelationFieldValue | null>;
	/** Stable identity of the item in its own collection. */
	referencedItemKey: Ref<CompositePrimaryKey | null>;
	/** Persisted FK reference value in update mode; live form value in create mode. */
	referencedItemReferenceKey?: Ref<CompositePrimaryKey | null>;
	disabled: MaybeRefOrGetter<boolean>;

	emitCreate: (payload: AnyItem) => void;
	emitUndoCreate: (payload: CreateItem) => void;
	emitConnect: (key: CompositePrimaryKey) => void;
	emitUndoConnect: (key: CompositePrimaryKey) => void;
	emitDisconnect: (key: CompositePrimaryKey) => void;
	emitUndoDisconnect: (key: CompositePrimaryKey) => void;
	emitDelete: (key: CompositePrimaryKey) => void;
	emitUndoDelete: (key: CompositePrimaryKey) => void;
	emitUpdate: (originalKey: CompositePrimaryKey | null, payload: AnyItem) => void;
}

export interface RelationFormControlProps {
	disabled?: boolean;
	disabledNotice?: string;
	fieldId?: GlobalSingleRelationFieldId;
}

/**
 * @internal
 */
const relationFormFieldContextKey = Symbol('relationFormFieldContextKey') as InjectionKey<RelationFormFieldContext>;

/** Provides the field's context to the controls rendered for it and returns its merged `disabled`. */
export function provideRelationFormFieldContext(context: RelationFormFieldContext) {
	provide(relationFormFieldContextKey, context);

	const base = useBaseFormField();
	return {
		disabled: computed(() => base.disabled.value || toValue(context.disabled)),
	};
}

/**
 * @internal
 */
function injectRelationFormFieldContext() {
	return inject(relationFormFieldContextKey, {
		field: computed(() => null),
		fieldValue: computed(() => null),
		referencedItemKey: computed(() => null),
		disabled: false,
		emitCreate: noop,
		emitUndoCreate: noop,
		emitConnect: noop,
		emitUndoConnect: noop,
		emitDisconnect: noop,
		emitUndoDisconnect: noop,
		emitDelete: noop,
		emitUndoDelete: noop,
		emitUpdate: noop,
	});
}

export function useRelationFormField<Kind extends 'to-many' | 'to-one' | never>(props: RelationFormControlProps) {
	const base = useBaseFormField(props);

	const context = injectRelationFormFieldContext() as RelationFormFieldContext<Kind>;
	const { findSingleRelationFieldById } = useWorkspaceSchemaStore();

	// Combine base disabled (from form field context) with relation disabled (from relation block props)
	const disabled = computed(() => {
		const baseDisabled = base.disabled.value;
		const relationDisabled = toValue(context?.disabled);
		return baseDisabled || relationDisabled || false;
	});

	const field = computed(() => {
		const fieldId = toValue(props.fieldId);

		if (fieldId) {
			return findSingleRelationFieldById(fieldId) ?? context?.field.value ?? null;
		}

		return context?.field.value ?? null;
	});

	return {
		...base,
		...context,
		disabled,
		field,
	};
}
